import { useMemo, useState } from 'react';

import { Button, DrawerHeaderButton } from 'design-system';
import { ArrowLeft } from 'lucide-react';
import { Permission } from '@/entities/permissions';
import PremiumFeatureMessage from '@/features/_shared/UpgradeToPremium/PremiumFeatureMessage';
import { usePermission } from '@/features/_shared/user';
import type { AppCatalogEntry } from '@/features/dashboard/components/apps';
import { APP_CATALOG } from '@/features/dashboard/components/apps';
import { Category } from '@/features/dashboard/components/apps/types';
import { DashboardDrawerLayout } from '@/features/dashboard/components/Drawer/components/DashboardDrawerLayout';
import { DrawerFooterButtons } from '@/features/dashboard/components/Drawer/components/DrawerFooterButtons';
import { useDrawerSteps } from '@/features/dashboard/components/Drawer/context/DrawerStepsContext';
import { DrawerHeader } from '@/features/dashboard/components/Drawer/DrawerHeader';
import SelectWidget from '@/features/dashboard/components/SelectWidget';
import { clearSelection, setPendingAppSymbols, SELECTOR_MODES } from '@/features/symbol-selector';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

const categories = [
  { key: Category.All, label: 'All apps' },
  { key: Category.Markets, label: 'Markets' },
  { key: Category.Charts, label: 'Charts' },
  { key: Category.Data, label: 'Data' }
];

// Lazy init to avoid issues with circular module deps at load time
let SPOT_INCOMPATIBLE_IDS: Set<string> | null = null;
let FWD_INCOMPATIBLE_IDS: Set<string> | null = null;

function getSpotIncompatibleIds(): Set<string> {
  SPOT_INCOMPATIBLE_IDS ??= new Set(
    APP_CATALOG.filter(a => (a.incompatibleWith as string[]).includes('spot')).map(a => a.id)
  );
  return SPOT_INCOMPATIBLE_IDS;
}
function getFwdIncompatibleIds(): Set<string> {
  FWD_INCOMPATIBLE_IDS ??= new Set(
    APP_CATALOG.filter(a => (a.incompatibleWith as string[]).includes('fwd')).map(a => a.id)
  );
  return FWD_INCOMPATIBLE_IDS;
}

export const AppSelector = () => {
  const { setDrawerStep, closeDrawer } = useDrawerSteps();
  const [category, setCategory] = useState<Category>(Category.All);
  const dispatch = useAppDispatch();
  const selectorMode = useAppSelector(s => s.symbolSelector.selectorMode);
  const selectorSymbols = useAppSelector(s => s.symbolSelector.symbols);

  const { hasPermission } = usePermission();

  // "From selector" mode: the drawer was opened via the "Create app" button
  const isFromSelector = selectorSymbols.length > 0;

  const filteredApps = useMemo(() => {
    if (category === Category.All) {
      return APP_CATALOG;
    }
    return APP_CATALOG.filter(app => app.category === category);
  }, [category]);

  const isAppDisabledByMode = useMemo(() => {
    if (!isFromSelector) return (_id: string) => false;
    if (selectorMode === SELECTOR_MODES.SPOT) {
      return (id: string) => getSpotIncompatibleIds().has(id);
    }
    if (selectorMode === SELECTOR_MODES.FORWARD_CURVE) {
      return (id: string) => getFwdIncompatibleIds().has(id);
    }
    return (_id: string) => false;
  }, [isFromSelector, selectorMode]);

  const [availableApps, premiumOnlyApps] = useMemo(() => {
    if (hasPermission(Permission.DashboardPremiumWidgets)) {
      return [filteredApps, [] as AppCatalogEntry[]];
    }
    const nonPremium = filteredApps.filter(app => !app.premium);
    const premium = filteredApps.filter(app => app.premium);
    return [nonPremium, premium];
  }, [filteredApps, hasPermission]);

  const handleSelectApp = (app: AppCatalogEntry) => {
    if (isFromSelector) {
      dispatch(setPendingAppSymbols(selectorSymbols));
      dispatch(clearSelection());
    }
    setDrawerStep(app.step);
  };

  return (
    <DashboardDrawerLayout
      footer={
        <DrawerFooterButtons
          disabled
          onDiscard={() => {}}
          onSave={() => {}}
        />
      }
      header={
        <DrawerHeader
          before={
            <DrawerHeaderButton
              label='Go back'
              onClick={closeDrawer}
            >
              <ArrowLeft />
            </DrawerHeaderButton>
          }
          buttons={null}
          title='Add new app'
        />
      }
      subheader={
        <>
          <div className='mb-6 flex flex-col gap-2'>
            <h2 className='font-heading text-xl font-semibold'>Choose app type</h2>
            <p className='text-sm font-medium text-muted-foreground'>
              Select an app to start configuring
            </p>
          </div>
          <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
            {categories.map(({ key, label }) => (
              <Button
                key={key}
                aria-pressed={category === key}
                className='h-9 w-full'
                variant={category === key ? 'default' : 'secondary'}
                onClick={() => {
                  setCategory(key);
                }}
              >
                {label}
              </Button>
            ))}
          </div>
        </>
      }
    >
      <div className='flex flex-col gap-6'>
        {premiumOnlyApps.length > 0 && (
          <div className='relative overflow-hidden select-none'>
            <div className='relative flex flex-col gap-6 blur-[3px]'>
              {premiumOnlyApps.map(app => (
                <SelectWidget
                  key={app.id}
                  badge={app.badge}
                  description={app.description}
                  icon={app.icon}
                  soon={app.soon}
                  testId={`select-widget-${app.id}`}
                  title={app.title}
                  onSelect={() => {}}
                />
              ))}
            </div>
            <div className='absolute inset-0 flex cursor-default flex-col justify-center bg-white/5 text-center text-white'>
              <PremiumFeatureMessage />
            </div>
          </div>
        )}
        {availableApps.map(app => (
          <SelectWidget
            key={app.id}
            badge={app.badge}
            description={app.description}
            disabled={isAppDisabledByMode(app.id)}
            icon={app.icon}
            soon={app.soon}
            testId={`select-widget-${app.id}`}
            title={app.title}
            onSelect={() => {
              handleSelectApp(app);
            }}
          />
        ))}
      </div>
    </DashboardDrawerLayout>
  );
};
